/**
 * RefractionSection Component
 *
 * Displays refraction data in a tabular format showing OD (right eye) and OS (left eye)
 * measurements including sphere, cylinder, axis, add, prism values, and visual acuity.
 * Provides an editable interface for updating refraction prescriptions.
 *
 * Follows Apple's design guidelines with consistent glass morphism UI and accessibility features.
 */

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { motion } from 'framer-motion';
import { CheckCircle, Edit3, Loader2, Save, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { Textarea } from './ui/textarea';

interface RefractionData {
  // OD (Right Eye) fields
  odSph?: string;
  odCyl?: string;
  odAxis?: string;
  odAdd?: string;
  odHPrism?: string;
  odVPrism?: string;
  odDVA?: string;
  odNVA?: string;

  // OS (Left Eye) fields
  osSph?: string;
  osCyl?: string;
  osAxis?: string;
  osAdd?: string;
  osHPrism?: string;
  osVPrism?: string;
  osDVA?: string;
  osNVA?: string;

  // Additional fields for different refraction types
  ouDVA?: string;
  ouNVA?: string;
  rxDate?: string;
  rxExpDate?: string;
  orderStatus?: string;
  notes?: string;
  habitualNotes?: string;
  finalNotes?: string;
  lensAddOns?: string;
  lensMessage?: string;
  patientInstructions?: string;
  usage?: string;
  distBPD?: string;
  nearBPD?: string;
  distMPDod?: string;
  distMPDos?: string;
  nearMPDod?: string;
  nearMPDos?: string;
  odPH?: string;
  osPH?: string;
  odLensType?: string;
  osLensType?: string;
  odLensMaterial?: string;
  osLensMaterial?: string;
  odLensColor?: string;
  osLensColor?: string;
  odLensCategory?: string;
  osLensCategory?: string;
  odLensBrand?: string;
  osLensBrand?: string;
  odBalanceLens?: string;
  osBalanceLens?: string;
  lensFrameColor?: string;
  lensFrameDesigner?: string;
  finalQuickAddons?: string;
  quickFinalStatus?: string;

  [key: string]: any; // Allow for other properties in the JSON
}

// Interface for the new API response structure
interface RefractionApiResponse {
  testKey: string;
  data: RefractionData[];
}

interface RefractionSectionProps {
  jsonContent: RefractionData | RefractionApiResponse[] | null;
  isLoading?: boolean;
  error?: string | null;
  index?: number;
  onUpdate: (updatedData: RefractionApiResponse[]) => Promise<void>;
  onAccept?: () => void;
}

const RefractionSection = ({
  jsonContent,
  isLoading = false,
  error = null,
  index = 0,
  onUpdate,
  onAccept,
}: RefractionSectionProps) => {
  const [currentData, setCurrentData] = useState<Record<
    string,
    RefractionData
  > | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editValues, setEditValues] = useState<Record<string, RefractionData>>(
    {}
  );
  const [isUpdating, setIsUpdating] = useState(false);
  const [acceptedSections, setAcceptedSections] = useState<Set<string>>(
    new Set()
  );

  // Helper function to process API response into RefractionData
  const processApiResponse = useCallback(
    (
      content: RefractionApiResponse[] | RefractionData | null
    ): Record<string, RefractionData> | null => {
      if (!content) return null;

      // If it's already processed data (single object), convert to record format
      if (!Array.isArray(content)) {
        return { refraction: content };
      }

      // Process API response array with new structure
      const processedData: Record<string, RefractionData> = {};

      content.forEach((item) => {
        if (item.data && item.data.length > 0) {
          const data = item.data[0]; // Get first item from data array

          // Extract the refraction type from testKey (e.g., "refractions.habitual" -> "habitual")
          const refractionType = item.testKey.replace('refractions.', '');

          processedData[refractionType] = data;
        }
      });

      return processedData;
    },
    []
  );

  // Helper function to convert refraction data back to API response format
  const convertToApiResponse = useCallback(
    (
      refractionData: Record<string, RefractionData>
    ): RefractionApiResponse[] => {
      const apiResponse: RefractionApiResponse[] = [];

      Object.entries(refractionData).forEach(([refractionType, data]) => {
        // Convert refraction type back to testKey format (e.g., "habitual" -> "refractions.habitual")
        const testKey = `refractions.${refractionType}`;

        apiResponse.push({
          testKey,
          data: [data], // Wrap in array as expected by API
        });
      });

      return apiResponse;
    },
    []
  );

  // Update local state when jsonContent changes
  useEffect(() => {
    const processedData = processApiResponse(jsonContent);
    setCurrentData(processedData);
  }, [jsonContent, processApiResponse]);

  // Handle updates to individual refraction sections
  const handleRefractionUpdate = useCallback(
    async (refractionType: string, updatedFields: Record<string, string>) => {
      if (!currentData) return;

      const updatedData: Record<string, RefractionData> = {
        ...currentData,
        [refractionType]: {
          ...currentData[refractionType],
          ...updatedFields,
        },
      };

      // Update local state optimistically
      setCurrentData(updatedData);

      try {
        // Convert the updated data to API response format and send it
        const apiResponseData = convertToApiResponse(updatedData);
        await onUpdate(apiResponseData);
      } catch (error) {
        // Revert local state on error
        const processedData = processApiResponse(jsonContent);
        setCurrentData(processedData);
        throw error;
      }
    },
    [
      currentData,
      jsonContent,
      onUpdate,
      processApiResponse,
      convertToApiResponse,
    ]
  );

  // Handle accepting all refraction sections
  const handleAcceptAll = useCallback(() => {
    if (!currentData) return;

    // Mark all sections as accepted
    const allSections = new Set(Object.keys(currentData));
    setAcceptedSections(allSections);

    // Call global accept
    if (onAccept) {
      onAccept();
    }

    console.log('✅ Accepted all refraction sections');
  }, [currentData, onAccept]);

  // Handle edit button click for all sections
  const handleEditAllClick = useCallback(() => {
    if (!currentData) return;

    // Combine all refraction data into a single edit object
    const allEditValues: Record<string, RefractionData> = {};

    // Include ALL refraction sections for editing
    Object.entries(currentData).forEach(([key, data]) => {
      allEditValues[key] = data;
    });

    console.log(
      '🔍 Opening edit dialog with all refraction values:',
      allEditValues
    );
    setEditValues(allEditValues);
    setIsEditDialogOpen(true);
  }, [currentData]);

  // Handle saving all edited values
  const handleSaveEdit = useCallback(async () => {
    if (!currentData) return;

    setIsUpdating(true);
    try {
      // Update all refraction data with edited values
      const updatedData: Record<string, RefractionData> = {
        ...currentData,
        ...editValues,
      };

      // Update local state optimistically
      setCurrentData(updatedData);

      // Convert the updated data to API response format and send it
      const apiResponseData = convertToApiResponse(updatedData);

      // Call onUpdate with the properly formatted data
      await onUpdate(apiResponseData);

      // Close the dialog
      setIsEditDialogOpen(false);
      setEditValues({});

      console.log('✅ Updated all refraction sections:', {
        editValues,
        apiFormat: apiResponseData,
      });
    } catch (error) {
      console.error('Failed to update refraction sections:', error);
      // Revert local state on error
      const processedData = processApiResponse(jsonContent);
      setCurrentData(processedData);
    } finally {
      setIsUpdating(false);
    }
  }, [
    editValues,
    currentData,
    onUpdate,
    jsonContent,
    processApiResponse,
    convertToApiResponse,
  ]);

  // Handle canceling edit
  const handleCancelEdit = useCallback(() => {
    setIsEditDialogOpen(false);
    setEditValues({});
  }, []);

  // Handle input changes in edit form for specific refraction type and field
  const handleInputChange = useCallback(
    (refractionType: string, field: string, value: string) => {
      setEditValues((prev) => ({
        ...prev,
        [refractionType]: {
          ...prev[refractionType],
          [field]: value,
        },
      }));
    },
    []
  );

  // Check if a refraction section has meaningful data
  const hasRefractionData = (data: RefractionData): boolean => {
    if (!data) return false;

    // Check for core refraction fields
    const coreFields = [
      'odSph',
      'odCyl',
      'odAxis',
      'odAdd',
      'odDVA',
      'odNVA',
      'osSph',
      'osCyl',
      'osAxis',
      'osAdd',
      'osDVA',
      'osNVA',
      'ouDVA',
      'ouNVA',
    ];

    return coreFields.some(
      (field) =>
        data[field] &&
        typeof data[field] === 'string' &&
        data[field].trim() !== '' &&
        data[field] !== 'NaN'
    );
  };

  // Get refraction sections that have data
  const refractionSections = currentData
    ? Object.entries(currentData)
        .filter(([key, data]) => hasRefractionData(data))
        .map(([key, data]) => ({
          key,
          title: key.charAt(0).toUpperCase() + key.slice(1) + ' Refraction',
          data,
        }))
    : [];

  // Check if any refraction sections have data
  const hasAnyRefractionData = refractionSections.length > 0;

  // Define table headers
  const headers = [
    '',
    'Sph',
    'Cyl',
    'Axis',
    'Add',
    'H Prism',
    'V Prism',
    'DVA',
    'NVA',
  ];

  // Helper function to get display value with fallback
  const getDisplayValue = (value: string | undefined) => {
    if (!value || value === 'NaN' || value.trim() === '') return '—';
    return value;
  };

  // Render refraction table for a given data set
  const renderRefractionTable = (data: RefractionData) => (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[820px]">
        <thead>
          <tr>
            {headers.map((header, idx) => (
              <th
                key={idx}
                className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-semibold text-slate-700 dark:text-slate-300 text-center whitespace-nowrap">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* OD (Right Eye) Row */}
          <tr>
            <td className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 text-center">
              <strong>OD</strong>
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odSph)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odCyl)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odAxis)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odAdd)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odHPrism)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odVPrism)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odDVA)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.odNVA)}
            </td>
          </tr>

          {/* OS (Left Eye) Row */}
          <tr>
            <td className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 text-center">
              <strong>OS</strong>
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osSph)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osCyl)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osAxis)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osAdd)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osHPrism)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osVPrism)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osDVA)}
            </td>
            <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
              {getDisplayValue(data.osNVA)}
            </td>
          </tr>

          {/* OU (Both Eyes) Row - if available */}
          {(data.ouDVA || data.ouNVA) && (
            <tr>
              <td className="border border-slate-200 dark:border-slate-700 bg-slate-200/50 dark:bg-slate-900/70 px-3 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 text-center">
                <strong>OU</strong>
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                —
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                {getDisplayValue(data.ouDVA)}
              </td>
              <td className="border border-slate-200 dark:border-slate-700 px-3 py-2 text-sm text-slate-700 dark:text-slate-300 text-center">
                {getDisplayValue(data.ouNVA)}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );

  if (!hasAnyRefractionData) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
        className="group">
        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg border border-white/30 dark:border-white/15 rounded-2xl p-4 shadow-md hover:shadow-lg transition-all duration-300">
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/8 pointer-events-none" />
          <div className="relative z-10">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-lg tracking-wide">
                  Refraction
                </h3>
                {isLoading && (
                  <div className="flex items-center gap-1 ml-2">
                    <Loader2 className="w-3 h-3 animate-spin text-eyevantage-primary" />
                    <span className="text-sm text-slate-500 dark:text-slate-400">
                      Loading...
                    </span>
                  </div>
                )}
              </div>
            </div>
            {isLoading ? (
              <motion.div
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex items-center gap-2 text-slate-500 dark:text-slate-400 text-md">
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{
                    duration: 1.5,
                    repeat: Infinity,
                    ease: 'linear',
                  }}>
                  <Loader2 className="w-4 h-4" />
                </motion.div>
                <motion.span
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}>
                  Fetching section content...
                </motion.span>
              </motion.div>
            ) : (
              <div className="text-slate-500 dark:text-slate-400 text-sm italic">
                No refraction data available yet
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.4, ease: 'easeOut' }}
        className="group space-y-4">
        {/* Main Refraction Card with Integrated Subsections */}
        <div className="relative bg-white/70 dark:bg-slate-900/70 backdrop-blur-lg border border-white/30 dark:border-white/15 rounded-2xl p-2 shadow-md hover:shadow-lg transition-all duration-300">
          <div className="absolute inset-[1px] rounded-2xl bg-gradient-to-t from-transparent via-transparent to-white/20 dark:to-white/8 pointer-events-none" />
          <div className="relative z-10">
            {/* Main Header */}
            <div className="p-2 border-b border-white/20 dark:border-white/10">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <h3 className="text-slate-800 dark:text-slate-200 font-semibold text-xl tracking-wide">
                    Refraction
                  </h3>
                  {isLoading && (
                    <div className="flex items-center gap-1 ml-2">
                      <Loader2 className="w-3 h-3 animate-spin text-eyevantage-primary" />
                      <span className="text-sm text-slate-500 dark:text-slate-400">
                        Loading...
                      </span>
                    </div>
                  )}
                </div>
                {hasAnyRefractionData && (
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditAllClick();
                      }}
                      size="sm"
                      className="h-8 px-3 bg-slate-100/80 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-gray-400/40 dark:border-white/20 text-slate-700 dark:text-slate-300 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                      disabled={isLoading}>
                      <Edit3 className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAcceptAll();
                      }}
                      size="sm"
                      className="h-8 px-3 bg-emerald-100/50 hover:bg-white dark:bg-slate-800/80 dark:hover:bg-slate-700/90 border border-emerald-600/60 dark:border-white/20 text-emerald-700 dark:text-emerald-700 rounded-2xl text-sm font-medium transition-all duration-300 backdrop-blur-sm shadow-sm"
                      disabled={isLoading}>
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Accept
                    </Button>
                  </div>
                )}
              </div>

              {error && (
                <div className="text-red-600 dark:text-red-400 text-sm mt-2">
                  <span className="font-medium">Error:</span> {error}
                </div>
              )}
            </div>

            {/* Individual Refraction Subsections */}
            <div className="p-2 space-y-2">
              {refractionSections.map((section, sectionIndex) => {
                const isAccepted = acceptedSections.has(section.key);

                // Create editable fields for the refraction data
                const editableFields = Object.entries(section.data).map(
                  ([key, value]) => ({
                    key,
                    label: key
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, (str) => str.toUpperCase()),
                    value: (value as string) || '',
                    placeholder: `Enter ${key
                      .replace(/([A-Z])/g, ' $1')
                      .toLowerCase()}`,
                  })
                );

                return (
                  <div
                    key={section.key}
                    className={`${
                      sectionIndex > 0
                        ? 'border-t border-white/20 dark:border-white/10 pt-4'
                        : ''
                    }`}>
                    {/* Subsection Header */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <h4 className="text-slate-700 dark:text-slate-300 font-semibold text-lg">
                          {section.title}
                        </h4>
                        {isAccepted && (
                          <CheckCircle className="w-4 h-4 text-emerald-500" />
                        )}
                      </div>
                    </div>

                    {/* Refraction Table */}
                    {renderRefractionTable(section.data)}

                    {/* Additional Notes if available */}
                    {(section.data.notes ||
                      section.data.habitualNotes ||
                      section.data.finalNotes) && (
                      <div className="mt-2 p-1">
                        <h5 className="text-md font-semibold text-slate-700 dark:text-slate-300">
                          Notes
                        </h5>
                        <p className="text-md text-slate-600 dark:text-slate-400">
                          {section.data.notes ||
                            section.data.habitualNotes ||
                            section.data.finalNotes}
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent
          className="max-w-6xl max-h-[90vh] overflow-y-auto bg-white/95 dark:bg-slate-900/95 backdrop-blur-lg border border-white/30 dark:border-white/15"
          onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="text-slate-800 dark:text-slate-200 font-semibold text-2xl">
              Edit Refraction
            </DialogTitle>
          </DialogHeader>

          <div
            className="space-y-4 overflow-y-auto"
            style={{ scrollbarWidth: 'none', height: '75vh' }}>
            {/* Render each refraction type as a separate section */}
            {Object.entries(editValues).map(
              ([refractionType, refractionData], index) => {
                const title =
                  refractionType.charAt(0).toUpperCase() +
                  refractionType.slice(1) +
                  ' Refraction';

                return (
                  <div
                    key={refractionType}
                    className="space-y-2 p-4 border bg-white dark:bg-slate-600/50 border-gray-400/40 dark:border-white/15 rounded-lg shadow-sm hover:shadow-md transition-all duration-300">
                    <h4 className="text-xl font-semibold text-slate-700 dark:text-blue-300 mb-4">
                      <span className="border-b-2 border-purple-500/80 dark:border-blue-300/80 h-1">
                        {title}
                      </span>
                    </h4>

                    {/* Table Header */}
                    <div className="grid grid-cols-9 bg-slate-300/50 dark:bg-blue-300/50 pl-2 pr-2 rounded-lg">
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300">
                        Eye
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        Sph
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        Cyl
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        Axis
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        Add
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        H Prism
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        V Prism
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        DVA
                      </div>
                      <div className="text-lg font-semibold text-slate-700 dark:text-slate-300 text-center">
                        NVA
                      </div>
                    </div>

                    {/* OD (Right Eye) Row */}
                    <div className="grid grid-cols-9 gap-2 items-center p-3 pb-0">
                      <div className="text-lg font-bold text-slate-700 dark:text-slate-300">
                        OD
                      </div>
                      <Input
                        value={refractionData?.odSph || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'odSph',
                            e.target.value
                          )
                        }
                        placeholder="e.g., -2.50"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.odCyl || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'odCyl',
                            e.target.value
                          )
                        }
                        placeholder="e.g., -0.75"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.odAxis || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'odAxis',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 90"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.odAdd || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'odAdd',
                            e.target.value
                          )
                        }
                        placeholder="e.g., +1.50"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.odHPrism || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'odHPrism',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 2Δ"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.odVPrism || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'odVPrism',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 1Δ"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.odDVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'odDVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 20/20"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.odNVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'odNVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., J1"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                    </div>

                    {/* OS (Left Eye) Row */}
                    <div className="grid grid-cols-9 gap-2 items-center p-3 pb-0">
                      <div className="text-lg font-bold text-slate-700 dark:text-slate-300">
                        OS
                      </div>
                      <Input
                        value={refractionData?.osSph || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'osSph',
                            e.target.value
                          )
                        }
                        placeholder="e.g., -2.25"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.osCyl || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'osCyl',
                            e.target.value
                          )
                        }
                        placeholder="e.g., -0.50"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.osAxis || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'osAxis',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 85"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.osAdd || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'osAdd',
                            e.target.value
                          )
                        }
                        placeholder="e.g., +1.50"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.osHPrism || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'osHPrism',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 1Δ"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.osVPrism || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'osVPrism',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 2Δ"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.osDVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'osDVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 20/25"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.osNVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'osNVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., J2"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                    </div>

                    {/* OU (Both Eyes) Row */}
                    <div className="grid grid-cols-9 gap-2 items-center p-3">
                      <div className="text-lg font-bold text-slate-700 dark:text-slate-300">
                        OU
                      </div>
                      <div className="col-span-5"></div>
                      <Input
                        value={refractionData?.ouDVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'ouDVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., 20/20"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                      <Input
                        value={refractionData?.ouNVA || ''}
                        onChange={(e) =>
                          handleInputChange(
                            refractionType,
                            'ouNVA',
                            e.target.value
                          )
                        }
                        placeholder="e.g., J1"
                        className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15 text-center"
                      />
                    </div>

                    {/* Additional Information for this refraction type */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-md text-slate-600 dark:text-slate-400">
                          Prescription Date
                        </Label>
                        <Input
                          value={refractionData?.rxDate || ''}
                          onChange={(e) =>
                            handleInputChange(
                              refractionType,
                              'rxDate',
                              e.target.value
                            )
                          }
                          placeholder="e.g., 2024-01-15"
                          className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-md text-slate-600 dark:text-slate-400">
                          Expiration Date
                        </Label>
                        <Input
                          value={refractionData?.rxExpDate || ''}
                          onChange={(e) =>
                            handleInputChange(
                              refractionType,
                              'rxExpDate',
                              e.target.value
                            )
                          }
                          placeholder="e.g., 2025-01-15"
                          className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-md text-slate-600 dark:text-slate-400">
                        Notes
                      </Label>
                      <Textarea
                        value={
                          refractionData?.notes ||
                          refractionData?.habitualNotes ||
                          refractionData?.finalNotes ||
                          ''
                        }
                        onChange={(e) => {
                          handleInputChange(
                            refractionType,
                            'notes',
                            e.target.value
                          );
                          handleInputChange(
                            refractionType,
                            'habitualNotes',
                            e.target.value
                          );
                          handleInputChange(
                            refractionType,
                            'finalNotes',
                            e.target.value
                          );
                        }}
                        placeholder="Enter any additional notes or observations..."
                        className="h-16 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"></Textarea>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-md text-slate-600 dark:text-slate-400">
                          Distance BPD
                        </Label>
                        <Input
                          value={refractionData?.distBPD || ''}
                          onChange={(e) =>
                            handleInputChange(
                              refractionType,
                              'distBPD',
                              e.target.value
                            )
                          }
                          placeholder="e.g., 62"
                          className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-md text-slate-600 dark:text-slate-400">
                          Near BPD
                        </Label>
                        <Input
                          value={refractionData?.nearBPD || ''}
                          onChange={(e) =>
                            handleInputChange(
                              refractionType,
                              'nearBPD',
                              e.target.value
                            )
                          }
                          placeholder="e.g., 60"
                          className="h-8 text-md bg-white/70 dark:bg-slate-800/70 border border-gray-400/40 dark:border-white/15"
                        />
                      </div>
                    </div>
                  </div>
                );
              }
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={(e) => {
                e.stopPropagation();
                handleCancelEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-white/80 dark:bg-slate-800/80 border border-gray-400/40 dark:border-white/20">
              <X className="w-4 h-4 mr-2" />
              Cancel
            </Button>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                handleSaveEdit();
              }}
              disabled={isUpdating}
              className="rounded-3xl bg-gradient-to-r from-purple-500 to-blue-800 hover:from-purple-500 hover:to-blue-800 text-white">
              {isUpdating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default RefractionSection;
